<template>
  <b-tabs
    class="w-100"
    v-model="tabIndex"
  >
    <b-tab
      :active="tab.active"
      :key="tab.name"
      :title="tab.title"
      v-for="tab in tabs"
      lazy
    >
      <b-container fluid>
        <!-- Aba Oscilografia - mostra o gráfico -->
        <template v-if="tab.name === 'oscillography'">
          <b-row>
            <graphic-report
              v-if="reportData && reportData.maneuvers"
              :key="`oscillography-chart-${tabIndex}`"
              :report-data="reportData"
              :loading="loading"
              :is-default-chart="isDefaultChart"
              :hide-maneuvers-section="hideManeuversSection"
              @get-chart-data="getChartData"
            >
              <slot name="chart-header-content" slot="chart-header-content" />
              <slot name="chart-header-buttons" slot="chart-header-buttons" />
              <slot name="chart-summary-row" slot="chart-summary-row" />
            </graphic-report>
            <div v-else class="empty-view">
              <s-empty-list />
            </div>
          </b-row>
        </template>

        <!-- Aba Diagnósticos - mostra toggle Gráfico/Informações -->
        <template v-else-if="tab.name === 'diagnostics'">
          <b-form-group>
            <b-form-radio-group
              buttons
              button-variant="outline-primary"
              size="sm"
              :checked="diagnosticsReportType"
              :value="diagnosticsReportType"
              :options="diagnosticsReportTypes"
              @input="updateDiagnosticsReportType"
            />
          </b-form-group>
          <b-row v-if="diagnosticsReportType === 'graphic'">
            <graphic-report
              v-if="reportData && reportData.maneuvers"
              :key="`diagnostics-chart-${tabIndex}-${diagnosticsReportType}`"
              :report-data="reportData"
              :loading="loading"
              :is-default-chart="isDefaultChart"
              :oscillography-chart-type="oscillographyChartTypes.DIAGNOSTICS"
              :hide-maneuvers-section="hideManeuversSection"
              @get-chart-data="getChartData"
            >
              <slot v-if="isDefaultChart" name="chart-header-content" slot="chart-header-content" />
              <slot name="chart-summary-row" slot="chart-summary-row" />
            </graphic-report>
          </b-row>
          <b-row v-else>
            <diagnostics-info-report
              :report-data="reportData"
              :is-default-chart="isDefaultChart"
            />
          </b-row>
        </template>

        <!-- Outras abas (Inf. adicionais, Alarmes, Conf. dos alarmes) -->
        <template v-else>
          <b-row align-h="end">
            <b-col
              cols="12"
              sm="12"
              md="8"
              lg="6"
              xl="4"
            >
              <s-search-input
                class="mb-3"
                v-model="tempSearch"
                v-s-trigger="searchChange"
                :placeholder="$t('reports.oscillography.view-report.search')"
              />
            </b-col>
          </b-row>
          <b-row
            v-for="maneuver in filteredManeuvers"
            :key="getManeuverKey(maneuver)"
          >
            <b-col cols="12">
              <info-report-group
                :maneuver="maneuver"
                :fields="maneuver.infoData[tab.accessorProperty]"
                :isDefaultChart="isDefaultChart"
                class="mb-3"
              />
            </b-col>
          </b-row>
          <b-row
            v-if="!filteredManeuvers.length"
          >
            <b-col cols="12">
              <s-empty-list />
            </b-col>
          </b-row>
        </template>
      </b-container>
    </b-tab>
  </b-tabs>
</template>
<script>
import { cleanIncludes, clearDiacritics } from '@/services/stringHelper';
import oscillographyChartTypes from '@/domain/enums/oscillographyChartTypes';
import InfoReportGroup from './InfoReportGroup';
import GraphicReport from '../GraphicReport';
import DiagnosticsInfoReport from './DiagnosticsInfoReport';

export default {
  components: {
    InfoReportGroup,
    GraphicReport,
    DiagnosticsInfoReport,
  },
  props: {
    reportData: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isDefaultChart: {
      type: Boolean,
      default: false,
    },
    hideManeuversSection: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tabIndex: 0,
    activeTabName: 'oscillography',
    diagnosticsReportType: 'graphic',
    tempSearch: null,
    search: null,
    oscillographyChartTypes,
  }),
  methods: {
    searchChange(search) {
      this.search = search;
    },
    filterManeuver({ name }) {
      return cleanIncludes(name, this.effectiveQuery, {
        clearValueToFind: false,
      });
    },
    filterManeuverFields(maneuverFields) {
      return maneuverFields.filter(({ name, value }) => cleanIncludes(value, this.effectiveQuery, {
        clearValueToFind: false,
      }) || cleanIncludes(name, this.effectiveQuery, {
        clearValueToFind: false,
      }));
    },
    getChartData(filter) {
      this.$emit('get-chart-data', filter);
    },
    getManeuverKey(maneuver) {
      return maneuver.maneuverKey || maneuver.maneuverUUID || maneuver.name;
    },
    updateDiagnosticsReportType(reportType) {
      this.diagnosticsReportType = reportType;
    },
    showHelpModal() {
      this.$emit('show-help-modal');
    },
    forceChartRerender() {
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    hasDataForTab(accessorProperty) {
      return this.maneuvers.some(
        maneuver => maneuver.infoData
        && maneuver.infoData[accessorProperty]
        && maneuver.infoData[accessorProperty].length > 0,
      );
    },
  },
  watch: {
    tabIndex(newIndex, oldIndex) {
      if (newIndex === 0 && oldIndex !== 0) {
        this.$nextTick(() => {
          this.forceChartRerender();
        });
      }
    },
  },
  mounted() {
    const aditionalFields = [
      'oscillographyUniqueIdentifierIED',
      'referenceOscillographyUniqueIdentifierIED',
    ];

    this.filteredManeuvers.map((maneuver) => {
      if (!maneuver || !maneuver.assetInfo || !maneuver.infoData) {
        return maneuver;
      }

      const { assetInfo, infoData } = maneuver;
      const fields = assetInfo.filter(item => aditionalFields.includes(item.key) && item.value !== '');

      if (!infoData.additionalInfo) {
        infoData.additionalInfo = [];
      }

      const alreadyAdded = infoData.additionalInfo.filter(item => item.name === this.$t(`reports.oscillography.view-report.${fields[0]?.key}`));

      if (alreadyAdded.length) {
        return maneuver;
      }
      infoData.additionalInfo = [
        ...infoData.additionalInfo,
        ...fields.map(field => ({
          acceptable: true,
          hasValue: true,
          maneuverId: undefined,
          name: this.$t(`reports.oscillography.view-report.${field.key}`),
          value: field.value,
        })),
      ];

      return maneuver;
    });
  },
  computed: {
    maneuvers() {
      return this.reportData?.maneuvers || [];
    },
    currentTabAccessorProperty() {
      return this.tabs[this.tabIndex].accessorProperty;
    },
    filteredManeuvers() {
      if (!this.effectiveQuery) {
        return this.maneuvers;
      }

      return this.maneuvers.reduce((localFilteredManeuvers, maneuver) => {
        if (this.filterManeuver(maneuver)) {
          localFilteredManeuvers.push(maneuver);
          return localFilteredManeuvers;
        }

        const filteredManeuverFields = this.filterManeuverFields(maneuver
          .infoData[this.currentTabAccessorProperty]);
        if (filteredManeuverFields.length) {
          localFilteredManeuvers.push({
            ...maneuver,
            infoData: {
              ...maneuver.infoData,
              [this.currentTabAccessorProperty]: filteredManeuverFields,
            },
          });
        }

        return localFilteredManeuvers;
      }, []);
    },
    effectiveQuery() {
      return this.search && this.search.length >= 2
        ? clearDiacritics(this.search).toLowerCase()
        : null;
    },
    allTabs() {
      return [
        {
          active: this.activeTabName === 'oscillography',
          name: 'oscillography',
          title: this.$t('reports.oscillography.view-report.oscillography'),
          accessorProperty: null,
        },
        {
          active: this.activeTabName === 'additional-info',
          name: 'additional-info',
          title: this.$t('reports.oscillography.view-report.additional-info'),
          accessorProperty: 'additionalInfo',
        },
        {
          active: this.activeTabName === 'diagnostics',
          name: 'diagnostics',
          title: this.$t('reports.oscillography.view-report.diagnostics'),
          accessorProperty: 'diagnostics',
        },
        {
          active: this.activeTabName === 'alarms',
          name: 'alarms',
          title: this.$t('reports.oscillography.view-report.alarms'),
          accessorProperty: 'alarms',
        },
        {
          active: this.activeTabName === 'alarm-settings',
          name: 'alarm-settings',
          title: this.$t('reports.oscillography.view-report.alarm-settings'),
          accessorProperty: 'alarmsConfigs',
        },
      ];
    },
    tabs() {
      return this.allTabs.filter(tab => {
        if (tab.name === 'oscillography') {
          return true;
        }

        if (tab.name === 'diagnostics') {
          return this.hasDiagnosticsData;
        }

        if (tab.accessorProperty) {
          return this.hasDataForTab(tab.accessorProperty);
        }

        return true;
      });
    },
    hasDiagnosticsData() {
      return this.maneuvers.some(
        maneuver => maneuver.infoData
        && maneuver.infoData.diagnostics
        && maneuver.infoData.diagnostics.length > 0
        && maneuver.infoData.diagnostics.some(field => field.hasValue),
      );
    },
    diagnosticsReportTypes() {
      return [
        {
          text: this.$t('reports.oscillography.view-report.graphic'),
          value: 'graphic',
        },
        {
          text: this.$t('reports.oscillography.view-report.info'),
          value: 'info',
        },
      ];
    },
  },
};
</script>

<style lang="sass" scoped>
  .empty-view
    display: flex
    justify-content: center
    align-items: center
    height: 100%
    width: 100%
</style>
